import { formatInTimeZone, toZonedTime } from "date-fns-tz";

// Business timezone (Australia/Sydney)
export const BUSINESS_TIMEZONE = "Australia/Sydney";

// Timezone options for the selector
export const TIMEZONE_OPTIONS = [
  { value: "local", label: "Your Local Time" },
  { value: BUSINESS_TIMEZONE, label: "Australia/Sydney (AEST/AEDT)" },
] as const;

export type TimezoneOption = (typeof TIMEZONE_OPTIONS)[number]["value"];

/**
 * Detects the user's browser timezone
 */
export function detectUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return BUSINESS_TIMEZONE;
  }
}

/**
 * Gets a human-readable timezone name with abbreviation
 */
export function getTimezoneDisplayName(timezone: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    });
    const parts = formatter.formatToParts(now);
    const timeZonePart = parts.find((p) => p.type === "timeZoneName");
    const abbrev = timeZonePart?.value || "";

    // Get readable name
    const nameFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "long",
    });
    const nameParts = nameFormatter.formatToParts(now);
    const longName =
      nameParts.find((p) => p.type === "timeZoneName")?.value || timezone;

    return `${longName} (${abbrev})`;
  } catch {
    return timezone;
  }
}

/**
 * Gets timezone abbreviation (e.g., "AEST", "EST", "PST")
 */
export function getTimezoneAbbreviation(timezone: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    });
    const parts = formatter.formatToParts(now);
    const timeZonePart = parts.find((p) => p.type === "timeZoneName");
    return timeZonePart?.value || "";
  } catch {
    return "";
  }
}

/**
 * Parse time string (e.g., "2:00 PM", "14:00") to hours and minutes
 */
function parseTimeString(time: string): { hours: number; minutes: number } {
  if (!time) return { hours: 0, minutes: 0 };
  // Handle 12-hour format (e.g., "2:00 PM", "12:30 AM")
  const amPmMatch = time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (amPmMatch) {
    let hours = parseInt(amPmMatch[1], 10);
    const minutes = parseInt(amPmMatch[2], 10);
    const meridiem = amPmMatch[3].toUpperCase();

    if (meridiem === "PM" && hours !== 12) {
      hours += 12;
    } else if (meridiem === "AM" && hours === 12) {
      hours = 0;
    }

    return { hours, minutes };
  }

  // Handle 24-hour format (e.g., "14:00", "09:30")
  const match24 = time.match(/(\d{1,2}):(\d{2})/);
  if (match24) {
    return {
      hours: parseInt(match24[1], 10),
      minutes: parseInt(match24[2], 10),
    };
  }

  return { hours: 0, minutes: 0 };
}

/**
 * Convert a time slot from one timezone to another
 * @param time - Time string (e.g., "2:00 PM" or "14:00")
 * @param date - Date string (e.g., "2024-01-15") or Date object
 * @param fromTimezone - Source timezone
 * @param toTimezone - Target timezone
 * @returns Converted time in 24-hour format (HH:mm)
 */
export function convertTimeToTimezone(
  time: string,
  date: string | Date,
  fromTimezone: string,
  toTimezone: string
): string {
  try {
    const { hours, minutes } = parseTimeString(time);

    // Parse date
    let dateObj: Date;
    if (typeof date === "string") {
      const [year, month, day] = date.split("-").map(Number);
      dateObj = new Date(year, month - 1, day, hours, minutes);
    } else {
      dateObj = new Date(date);
      dateObj.setHours(hours, minutes, 0, 0);
    }

    // Create a date in the source timezone
    const sourceDate = new Date(
      dateObj.toLocaleString("en-US", { timeZone: fromTimezone })
    );
    const targetDate = new Date(
      dateObj.toLocaleString("en-US", { timeZone: toTimezone })
    );

    // Calculate offset difference
    const diffMs = targetDate.getTime() - sourceDate.getTime();

    // Apply the offset
    const convertedDate = new Date(dateObj.getTime() + diffMs);

    // Format to HH:mm
    return formatInTimeZone(convertedDate, toTimezone, "HH:mm");
  } catch {
    return time;
  }
}

/**
 * Convert time to 12-hour format with AM/PM
 */
export function formatTimeTo12Hour(time: string): string {
  if (!time) return "";
  const { hours, minutes } = parseTimeString(time);
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  return `${displayHour}:${minutes.toString().padStart(2, "0")} ${ampm}`;
}

/**
 * Format time with timezone abbreviation
 * @param time - Time string
 * @param timezone - Timezone identifier
 * @returns Formatted time with abbreviation (e.g., "2:00 PM AEST")
 */
export function formatTimeWithTimezone(time: string, timezone: string): string {
  const formatted = formatTimeTo12Hour(time);
  const abbrev = getTimezoneAbbreviation(timezone);
  return abbrev ? `${formatted} ${abbrev}` : formatted;
}

/**
 * Get the effective timezone based on user selection
 * @param selection - "local" or a specific timezone
 * @returns The actual timezone identifier
 */
export function getEffectiveTimezone(selection: TimezoneOption): string {
  if (selection === "local") {
    return detectUserTimezone();
  }
  return selection;
}

/**
 * Check if two timezones are effectively the same (same offset at current time)
 */
export function areTimezonesEqual(tz1: string, tz2: string): boolean {
  try {
    const now = new Date();
    const offset1 = new Date(
      now.toLocaleString("en-US", { timeZone: tz1 })
    ).getTime();
    const offset2 = new Date(
      now.toLocaleString("en-US", { timeZone: tz2 })
    ).getTime();
    return offset1 === offset2;
  } catch {
    return tz1 === tz2;
  }
}

/**
 * Get UTC offset string for a timezone (e.g., "+10:00", "-05:00")
 */
export function getTimezoneOffset(timezone: string): string {
  try {
    const now = new Date();
    const formatted = formatInTimeZone(now, timezone, "xxx");
    return formatted;
  } catch {
    return "";
  }
}

/**
 * Get business timezone display string (e.g., "Australia/Sydney (AEDT +11)" or "Australia/Sydney (AEST +10)")
 * Dynamically computes the abbreviation (AEST vs AEDT) and UTC offset (+10 vs +11) based on the target date.
 */
export function getBusinessTimezoneDisplay(date?: Date | string | null): string {
  try {
    const targetDate = date ? new Date(date) : new Date();
    const validDate = isNaN(targetDate.getTime()) ? new Date() : targetDate;
    const longName = formatInTimeZone(validDate, BUSINESS_TIMEZONE, "zzzz");
    const isDST = longName.includes("Daylight");
    const abbrev = isDST ? "AEDT" : "AEST";
    const offset = formatInTimeZone(validDate, BUSINESS_TIMEZONE, "xxx").replace(":00", "");
    return `Australia/Sydney (${abbrev} ${offset})`;
  } catch {
    return "Australia/Sydney (AEST/AEDT)";
  }
}

/**
 * Storage key for persisting timezone preference
 */
export const TIMEZONE_STORAGE_KEY = "alex_booking_timezone";

/**
 * Save timezone preference to localStorage
 */
export function saveTimezonePreference(timezone: TimezoneOption): void {
  try {
    localStorage.setItem(TIMEZONE_STORAGE_KEY, timezone);
  } catch {
    // localStorage might not be available
  }
}

/**
 * Load timezone preference from localStorage
 */
export function loadTimezonePreference(): TimezoneOption {
  try {
    const saved = localStorage.getItem(TIMEZONE_STORAGE_KEY);
    if (saved === "local" || saved === BUSINESS_TIMEZONE) {
      return saved;
    }
  } catch {
    // localStorage might not be available
  }
  return "local";
}
